import {
	Autocomplete,
	type SxProps,
	TextField,
	type Theme,
	useMediaQuery,
} from '@mui/material'
import List from '@mui/material/ImageList'
import ImageListItem from '@mui/material/ImageListItem'
import ListItemButton from '@mui/material/ListItemButton'
import Pagination from '@mui/material/Pagination'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import 'react-lazy-load-image-component/src/effects/blur.css'
import React, { type ChangeEvent, useState } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import AuthContext from '../../../contexts/auth'
import { useFetchWithToken } from '../../../hooks/useFetch'
import type { Resource } from '../../../services/galleryService'
import theme from '../../../styles/theme'
import type { Image } from '../../../types/Image'

interface ImageListProps {
	currentImage: Image | null
	setCurrentImage: React.Dispatch<React.SetStateAction<Image | null>>
	sx?: SxProps<Theme>
	resource: Resource
}

type OptionType = {
	label: string
	value: Resource | ''
}

const options: OptionType[] = [
	{ label: 'Todos', value: '' },
	{ label: 'Receita', value: 'POST' },
	{ label: 'Blog', value: 'BLOG_POST' },
	{ label: 'Categoria', value: 'CATEGORY' },
	{ label: 'Subcategoria', value: 'SUBCATEGORY' },
	{ label: 'Popup', value: 'POPUP' },
	{ label: 'Passo a passo', value: 'STEP' },
]

function ImageList({
	currentImage,
	setCurrentImage,
	sx,
	resource,
}: ImageListProps) {
	const [page, setPage] = React.useState(1)

	const smBreakpoint = useMediaQuery(theme.breakpoints.down('sm'))
	const mdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))

	const cols = smBreakpoint ? 3 : mdBreakpoint ? 5 : 10

	const PER_PAGE = smBreakpoint ? 18 : mdBreakpoint ? 25 : 40

	const [term, setTerm] = useState('')

	const resourceOption: OptionType = options.find(
		option => option.value === resource
	) ?? {
		label: 'Todos',
		value: '',
	}

	const [resourceFilter, setResourceFilter] =
		useState<OptionType>(resourceOption)

	const handlePageChange = (_: ChangeEvent<unknown>, page: number) => {
		setPage(page)
	}

	const { user } = React.useContext(AuthContext)

	const { isLoading, data } = useFetchWithToken<{
		count: number
		images: Image[]
	}>(
		`/image?resource=${resourceFilter.value}&query=${term}&take=${PER_PAGE}&skip=${
			PER_PAGE * (page - 1)
		}`,
		user?.access_token as string
	)

	const count = (data && Math.ceil(data.count / PER_PAGE)) || 1

	return (
		<Stack justifyContent='space-evenly' alignItems='center' sx={sx}>
			<Stack
				direction={smBreakpoint ? 'column' : 'row'}
				justifyContent='space-between'
				spacing={2}
				sx={{ width: '100%' }}
			>
				<Autocomplete
					size='small'
					options={options}
					sx={{
						width: 250,
						[theme.breakpoints.down('sm')]: { width: '100%' },
					}}
					value={options.find(option => option.value === resourceFilter.value)}
					getOptionLabel={value => value.label}
					disableClearable
					onChange={(_, { value }) => {
						setResourceFilter(
							options.find(option => option.value === value) as OptionType
						)
					}}
					renderInput={params => <TextField {...params} label='Recurso' />}
				/>
				<TextField
					size='small'
					placeholder='pesquisar pelo nome...'
					sx={{
						width: 250,
						[theme.breakpoints.down('sm')]: { width: '100%' },
					}}
					onChange={e => setTerm(e.target.value)}
				/>
			</Stack>
			<List cols={cols} rowHeight={100}>
				{!isLoading && data
					? data.images.map(image => (
							<ImageListItem key={image.id}>
								<ListItemButton
									onClick={() => setCurrentImage(image)}
									selected={image.id === currentImage?.id}
									sx={{
										padding: '3px',
										'&.Mui-selected': { backgroundColor: '#f44336' },
										backgroundColor: '#f0f0f0',
									}}
								>
									<LazyLoadImage
										src={image.url}
										alt={image.alt}
										width='100%'
										height={100}
										effect='blur'
										style={{ objectFit: 'cover' }}
									/>
								</ListItemButton>
							</ImageListItem>
						))
					: Array.from({ length: PER_PAGE }).map((_, index) => (
							<ImageListItem key={index}>
								<Skeleton variant='rectangular' height={100} animation='wave' />
							</ImageListItem>
						))}
			</List>
			<Pagination count={count} page={page} onChange={handlePageChange} />
		</Stack>
	)
}

export default ImageList
